import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import {
  RequestSource,
  UserActionRequest_ActionType,
} from '@weave/schema-gen-ts/dist/schemas/threads-of-success/v2/api.pb';
import Lottie from 'react-lottie-player';
import { ThreadsOfSuccessV2Mutations, ThreadsOfSuccessV2Queries } from '@frontend/api-threads-of-success-v2';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { WeaveRewindPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { Button, Text, useModalControl } from '@frontend/design-system';
import officeIntroAnimation from './animations/officeIntro.json';
import recordSpinningAnimation from './animations/recordSpinning.json';
import userIntroAnimation from './animations/userIntro.json';
import { CUSTOM_COLOR } from './styles';
import { useWeaveRewindFeatureFlag } from './use-weave-rewind-feature-flag';
import { getUpdatedLocationShortType, getUpdatedUserBadgeType, getUpdatedUserShortType } from './utils';
import { WeaveRewindModal } from './weave-rewind-modal';

type RewindCardType = 'dashboard' | 'profile';

const CardContent = ({
  onDismiss,
  onView,
  type,
}: {
  onDismiss: () => void;
  onView: () => void;
  type: RewindCardType;
}) => {
  const { t } = useTranslation('dashboard');
  const prefix = type === 'dashboard' ? WeaveRewindPrefixes.DashboardCard : WeaveRewindPrefixes.ProfileCard;
  const showDismissButton = type === 'dashboard';

  return (
    <>
      <Text color='white' weight='bold'>
        {t('Your 2025 Weave Rewind is ready!')}
      </Text>
      <Text color='white' size='medium'>
        {t('See all of the accomplishments you and your team had through 2025!')}
      </Text>
      <div css={{ paddingTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        {showDismissButton && (
          <Button
            variant='tertiary'
            css={{ color: theme.colors.neutral50 }}
            onClick={onDismiss}
            trackingId={prefix + '-dismiss-button'}
          >
            {t('Dismiss')}
          </Button>
        )}
        <Button variant='primary' onClick={onView} trackingId={prefix + '-view-button'}>
          {t('View My Rewind!')}
        </Button>
      </div>
      <Lottie
        loop={true}
        play={true}
        animationData={recordSpinningAnimation}
        style={{
          width: 120,
          height: 120,
          position: 'absolute',
          bottom: -48,
          left: -24,
        }}
      />
    </>
  );
};

type Props = {
  type: RewindCardType;
};

export const WeaveRewind = ({ type }: Props) => {
  const [dismissed, setDismissed] = useState(false);
  const { modalProps, triggerProps } = useModalControl();
  const { selectedLocationIds: locationIds } = useAppScopeStore();

  const weaveRewindEnabled = useWeaveRewindFeatureFlag();

  const { data: shortsData, isLoading: shortsIsLoading } = ThreadsOfSuccessV2Queries.useGetShorts(
    {
      locationIds,
      requestSource:
        type === 'profile' ? RequestSource.REQUEST_SOURCE_USER_PROFILE : RequestSource.REQUEST_SOURCE_NWX_DASHBOARD,
    },
    {
      enabled: weaveRewindEnabled,
    }
  );

  const { mutate: mutateUserAction } = ThreadsOfSuccessV2Mutations.useUserAction();

  const shorts = useMemo(() => {
    if (!shortsData || shortsIsLoading) return [];
    const locationShorts =
      shortsData?.locationShorts?.map((short) => ({
        type: short.type,
        animation: getUpdatedLocationShortType(short),
      })) || [];

    const userBadgeShorts =
      shortsData?.userBadgeShorts?.map((short) => ({
        type: short.type,
        animation: getUpdatedUserBadgeType(short),
      })) || [];

    const userShorts =
      shortsData?.userShorts?.map((short) => ({
        type: short.type,
        animation: getUpdatedUserShortType(short),
      })) || [];

    return [
      { type: 'userIntro', animation: userShorts.length || userBadgeShorts.length ? userIntroAnimation : null },
      ...userShorts,
      ...userBadgeShorts,
      { type: 'officeIntro', animation: locationShorts.length ? officeIntroAnimation : null },
      ...locationShorts,
    ].filter((short) => short.animation);
  }, [shortsData, shortsIsLoading]);

  const onDismiss = () => {
    setDismissed(true);
    mutateUserAction({
      locationIds,
      actionType: UserActionRequest_ActionType.ACTION_TYPE_DISMISS_REWIND,
    });
  };

  const onView = () => {
    if (type === 'dashboard') setDismissed(true);
    triggerProps.onClick();
    mutateUserAction({
      locationIds,
      actionType: UserActionRequest_ActionType.ACTION_TYPE_VIEW_MY_REWIND,
    });
  };

  if (!weaveRewindEnabled || shortsIsLoading || shorts.length === 0) {
    return null;
  }

  // Always show in profile panel
  if (type === 'profile') {
    return (
      <section css={{ width: '100%', padding: theme.spacing(3) }}>
        <div css={[cardStyles, { position: 'relative' }]}>
          <CardContent onDismiss={onDismiss} onView={onView} type={type} />
        </div>
        <WeaveRewindModal modalProps={modalProps} shorts={shorts} />
      </section>
    );
  }

  // Show dismissible card in dashboard
  return (
    <>
      {!dismissed && (
        <div
          css={[
            cardStyles,
            {
              position: 'fixed',
              bottom: theme.spacing(2),
              right: theme.spacing(9),
              width: 367,
              zIndex: theme.zIndex.highest,
            },
          ]}
        >
          <CardContent onDismiss={onDismiss} onView={onView} type={type} />
        </div>
      )}
      <WeaveRewindModal modalProps={modalProps} shorts={shorts} />
    </>
  );
};

const cardStyles = css({
  backgroundColor: CUSTOM_COLOR,
  borderRadius: theme.borderRadius.medium,
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  flexShrink: 0,
  overflow: 'hidden', // This clips the animation overflow
});
